
import { DashboardNavbar } from "@/components/dashboard/DashboardNavbar";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileSpreadsheet, Plus, Search, Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { CsvImportDialog } from "@/components/crm/CsvImportDialog";
import { AddClientDialog } from "@/components/dashboard/AddClientDialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { FullPageTableSkeleton } from "@/components/ui/skeleton-block";

interface Client {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  status: 'active' | 'inactive' | 'lead';
  last_interaction: string | null;
  created_at: string;
}

export default function ClientsPage() {
  const { user } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [addClientDialogOpen, setAddClientDialogOpen] = useState(false);

  const loadClients = async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      toast.error('Failed to load clients');
      console.error('Error loading clients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Clients | Figgy.link";
  }, []);

  useEffect(() => {
    if (user) {
      loadClients();
    }
  }, [user]);

  const filteredClients = clients.filter(client => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const fullName = `${client.first_name} ${client.last_name}`.toLowerCase();
    return fullName.includes(q) || (client.phone ?? '').includes(q);
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardNavbar />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-semibold">Clients</h1>
              <p className="text-muted-foreground">
                Manage and organize your client base
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setImportDialogOpen(true)}
                className="flex items-center"
              >
                <Upload className="mr-2 h-4 w-4" />
                Import Clients
              </Button>
              <Button
                className="bg-figgy-500 hover:bg-figgy-600"
                onClick={() => setAddClientDialogOpen(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Client
              </Button>
            </div>
          </div>

          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {isLoading ? <FullPageTableSkeleton rows={6} cols={4} /> : (
          <div className="bg-white rounded-lg shadow">
            {filteredClients.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Interaction
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredClients.map((client) => (
                      <tr key={client.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          {client.first_name} {client.last_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            {client.email && (
                              <div>{client.email}</div>
                            )}
                            {client.phone && (
                              <div className="text-gray-500">{client.phone}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex rounded-full px-2 text-xs font-semibold capitalize
                            ${client.status === 'active' ? 'bg-green-100 text-green-800' : 
                              client.status === 'inactive' ? 'bg-gray-100 text-gray-800' : 
                              'bg-yellow-100 text-yellow-800'}`}>
                            {client.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {client.last_interaction ? 
                            new Date(client.last_interaction).toLocaleDateString() : 
                            'Never'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : clients.length > 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm text-muted-foreground">No clients match your search.</p>
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                  <FileSpreadsheet className="h-6 w-6 text-gray-600" />
                </div>
                <h3 className="mt-2 text-sm font-semibold text-gray-900">No clients yet</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Import your existing clients or add them manually
                </p>
                <div className="mt-6 flex justify-center gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setImportDialogOpen(true)}
                    className="flex items-center"
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Import Clients
                  </Button>
                  <Button
                    className="bg-figgy-500 hover:bg-figgy-600"
                    onClick={() => setAddClientDialogOpen(true)}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Client
                  </Button>
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      <CsvImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onImportSuccess={loadClients}
      />

      <AddClientDialog
        open={addClientDialogOpen}
        onOpenChange={setAddClientDialogOpen}
        onClientAdded={loadClients}
      />
    </div>
  );
}
